import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Brain, Eye, MessageCircle, Hand } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface GCSScores {
  eye: number;
  verbal: number;
  motor: number;
}

const Index = () => {
  const [scores, setScores] = useState<GCSScores>({
    eye: 0,
    verbal: 0,
    motor: 0
  });

  const [selectedValues, setSelectedValues] = useState({
    eye: '',
    verbal: '',
    motor: ''
  });

  const eyeOptions = [
    { value: '4', label: 'Spontan', points: 4 },
    { value: '3', label: 'Bei Ansprache', points: 3 },
    { value: '2', label: 'Bei Schmerzreiz', points: 2 },
    { value: '1', label: 'Kein Öffnen der Augen', points: 1 }
  ];

  const verbalOptions = [
    { value: '5', label: 'konversationsfähig, orientiert', points: 5 },
    { value: '4', label: 'konversationsfähig, desorientiert', points: 4 },
    { value: '3', label: 'Einzelworte ("Wortsalat")', points: 3 },
    { value: '2', label: 'sinnlose Laute', points: 2 },
    { value: '1', label: 'keine verbale Antwort', points: 1 }
  ];

  const motorOptions = [
    { value: '6', label: 'Befolgt Aufforderungen', points: 6 },
    { value: '5', label: 'gezielte Bewegung bei Schmerzreiz', points: 5 },
    { value: '4', label: 'ungezielte Bewegung auf Schmerzreiz', points: 4 },
    { value: '3', label: 'Beugesynergismen', points: 3 },
    { value: '2', label: 'Strecksynergismen', points: 2 },
    { value: '1', label: 'keine motorische Reaktion', points: 1 }
  ];

  const handleScoreChange = (category: keyof GCSScores, value: string) => {
    const points = parseInt(value);
    setScores(prev => ({ ...prev, [category]: points }));
    setSelectedValues(prev => ({ ...prev, [category]: value }));
  };

  const totalScore = scores.eye + scores.verbal + scores.motor;
  const isComplete = scores.eye > 0 && scores.verbal > 0 && scores.motor > 0;

  const getInterpretation = (score: number) => {
    if (score >= 13) return { level: 'leicht', color: 'bg-green-100 text-green-800 border-green-200' };
    if (score >= 9) return { level: 'moderat', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    return { level: 'schwer', color: 'bg-red-100 text-red-800 border-red-200' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Brain className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              Glasgow Coma Scale (GCS) – Rechner
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Die GCS dient zur Beurteilung des Bewusstseinszustands. Sie setzt sich aus Augenöffnung, 
            verbaler Antwort und motorischer Antwort zusammen.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Augenöffnung */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <Eye className="h-5 w-5" />
                Öffnen der Augen (E)
              </CardTitle>
              <CardDescription>Wählen Sie die beste Reaktion</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={selectedValues.eye}
                onValueChange={(value) => handleScoreChange('eye', value)}
                className="space-y-3"
              >
                {eyeOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value={option.value} id={`eye-${option.value}`} />
                    <Label 
                      htmlFor={`eye-${option.value}`} 
                      className="flex-1 cursor-pointer text-sm leading-relaxed"
                    >
                      {option.label}
                    </Label>
                    <Badge variant="outline" className="ml-auto">
                      {option.points}
                    </Badge>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Verbale Antwort */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <MessageCircle className="h-5 w-5" />
                Beste verbale Antwort (V)
              </CardTitle>
              <CardDescription>Wählen Sie die beste Reaktion</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={selectedValues.verbal}
                onValueChange={(value) => handleScoreChange('verbal', value)}
                className="space-y-3"
              >
                {verbalOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value={option.value} id={`verbal-${option.value}`} />
                    <Label 
                      htmlFor={`verbal-${option.value}`} 
                      className="flex-1 cursor-pointer text-sm leading-relaxed"
                    >
                      {option.label}
                    </Label>
                    <Badge variant="outline" className="ml-auto">
                      {option.points}
                    </Badge>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Motorische Antwort */}
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <Hand className="h-5 w-5" />
                Beste motorische Antwort (M)
              </CardTitle>
              <CardDescription>Wählen Sie die beste Reaktion</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={selectedValues.motor}
                onValueChange={(value) => handleScoreChange('motor', value)}
                className="space-y-3"
              >
                {motorOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value={option.value} id={`motor-${option.value}`} />
                    <Label 
                      htmlFor={`motor-${option.value}`} 
                      className="flex-1 cursor-pointer text-sm leading-relaxed"
                    >
                      {option.label}
                    </Label>
                    <Badge variant="outline" className="ml-auto">
                      {option.points}
                    </Badge>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        </div>

        {/* Ergebnis */}
        <Card className="shadow-xl border-0 bg-white">
          <CardHeader>
            <CardTitle className="text-center text-xl">GCS-Berechnung</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isComplete ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Bitte alle drei Komponenten auswählen.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    GCS-Gesamtpunktzahl: {totalScore}
                  </div>
                  <div className="text-lg text-gray-600">
                    (E: {scores.eye} / V: {scores.verbal} / M: {scores.motor})
                  </div>
                </div>
                
                <div className="flex justify-center">
                  <Badge 
                    className={`text-lg px-4 py-2 ${getInterpretation(totalScore).color}`}
                    variant="outline"
                  >
                    {getInterpretation(totalScore).level}
                  </Badge>
                </div>

                {totalScore <= 8 && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      <strong>Achtung:</strong> Potenziell lebensbedrohlicher Zustand. 
                      Sofortige medizinische Intervention erforderlich.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2">Interpretation:</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div>• <strong>13-15 Punkte:</strong> Leichte Bewusstseinsstörung</div>
                    <div>• <strong>9-12 Punkte:</strong> Moderate Bewusstseinsstörung</div>
                    <div>• <strong>3-8 Punkte:</strong> Schwere Bewusstseinsstörung</div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Definition */}
        <Card className="mt-6 shadow-lg border-0 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="text-blue-800">Definition</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              Die Glasgow Coma Scale, kurz GCS, ist ein Bewertungsschema für Bewusstseins- und 
              Hirnfunktionsstörungen nach einem Schädel-Hirn-Trauma. Sie wurde 1974 von 
              Graham Teasdale und Bryan Jennett an der Universität Glasgow entwickelt.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Index;
